import React from 'react';
import styled from '@tanium/react-emotion-9';
import { NotificationContainer } from '@tanium/react-persistent-notifications';
import { css } from '@tanium/style-system';
import { FixedHeader, PageFixedHeaderProps } from './FixedHeader';
import { PageFill } from './themeDefinitions';

// This is a semi-arbitrary number based on the rendered height of the home page
// header. If necessary, it could be made configurable via props.
const FIXED_HEADER_HEIGHT = 77;

export interface Props {
  /**
   * The page's content.
   */
  children: React.ReactNode;

  /**
   * An optional render prop for a fixed, optionally collapsible header to
   * display above the page content.
   */
  fixedHeader?: React.FC<PageFixedHeaderProps>;
}

const BaseWrapper = styled('div')(
  css({
    bg: PageFill,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  }),
);

const BaseContent = styled('div', {
  shouldForwardProp: (p) => p !== 'hasFixedHeader',
})<{ hasFixedHeader: boolean }>(
  css({
    p: 2,
    pb: 0,
    display: 'flex',
    flexDirection: 'column',
    flexBasis: '100%',
    '::after': {
      content: '""',
      pt: 3,
    },
  }),
  ({ hasFixedHeader }) =>
    hasFixedHeader &&
    css((theme) => ({
      pt: `${theme.space[3] + FIXED_HEADER_HEIGHT}px`,
    })),
);

const NotificationContainerWrapper = styled('div')({
  flexGrow: 0,
});

const ContentWrapper = styled('div')({
  flexGrow: 0,
  flexShrink: 1,
  flexBasis: '100%',
});

export function PageBase({ children, fixedHeader }: Props) {
  return (
    <BaseWrapper>
      {fixedHeader && <FixedHeader header={fixedHeader} />}
      <BaseContent hasFixedHeader={!!fixedHeader}>
        <NotificationContainerWrapper>
          <NotificationContainer />
        </NotificationContainerWrapper>
        <ContentWrapper>{children}</ContentWrapper>
      </BaseContent>
    </BaseWrapper>
  );
}
